/**
 * YAML writer — converts HubAsCodePayload to hub.yaml + agents/<slug>.yaml + agents/<slug>.md files
 * Reverse of parser.ts
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { ensureUniqueSlug } from '@wayai/core';
import { slugify, resolveHubYamlPath } from './utils.js';
import { writeResourceFileTree } from './resource-files.js';
import { assertAttachmentsAreLocalPaths } from './eval-attachments.js';
import { CLAUDE_MD_SHIM, perHubAgentsMd, writeIfAbsent } from './workspace-files.js';
import { fileHasBytes, writeFileNoFollow, type FileChangeLog } from './fs-safety.js';
import type { HubAsCodePayload, HubAsCodeAgent, HubAsCodeResource, HubAsCodeEval, HubAsCodeJourney } from './types.js';

export type { FileChangeLog } from './fs-safety.js';

/**
 * Write `content` to `absPath` only when it differs from what's already on disk, recording the
 * hub-relative path in `log.changed` when it does. A byte-identical file is left untouched, so
 * writeHubFolder is idempotent (no mtime churn for editors/watchers/git) and a caller can NAME
 * exactly what changed (gh #3410). Change detection is lstat-guarded via `fileHasBytes`.
 *
 * The write goes through `writeFileNoFollow` (bounded by `hubFolder`): a symlinked destination or
 * symlinked ancestor — e.g. an attacker-authored workspace with `hub.yaml -> /tmp/victim` or
 * `agents/ -> /tmp/outside` — must not redirect a pull's write outside the hub folder. Matches the
 * no-follow discipline the resource/attachment writers already use. A rejected write is warned and
 * NOT recorded as changed.
 */
export function writeFileIfChanged(hubFolder: string, absPath: string, content: string, log: FileChangeLog): void {
  const buf = Buffer.from(content, 'utf-8');
  if (fileHasBytes(absPath, buf)) return;
  if (writeFileNoFollow(hubFolder, absPath, buf)) {
    log.changed.push(path.relative(hubFolder, absPath));
  } else {
    console.warn(`  Warning: skipping ${path.relative(hubFolder, absPath)} (destination escapes the hub folder via a symlink)`);
  }
}

export const YAML_DUMP_OPTIONS: yaml.DumpOptions = {
  lineWidth: -1,      // don't wrap lines
  noRefs: true,        // don't use YAML anchors
  quotingType: '"',
  forceQuotes: false,
  sortKeys: false,
};

// Shared between writeEvalYamlFiles (pull) and `wayai eval capture` so the on-disk shape stays identical.
export function buildEvalYamlObject(evalEntry: HubAsCodeEval, slug: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (evalEntry.id) out.id = evalEntry.id;
  if (evalEntry.name !== slug) out.name = evalEntry.name;
  out.agent = evalEntry.agent;
  if (evalEntry.agent_id) out.agent_id = evalEntry.agent_id;
  if (typeof evalEntry.runs === 'number' && evalEntry.runs !== 1) {
    out.runs = evalEntry.runs;
  }
  if (evalEntry.enabled === false) {
    out.enabled = false;
  }
  if (evalEntry.history && evalEntry.history.length > 0) {
    for (const turn of evalEntry.history) assertAttachmentsAreLocalPaths(turn, `eval "${evalEntry.name}" history`);
    out.history = evalEntry.history;
  }
  // Eval-session seed fixture (gh #3090) — emit near the top so the seed dependency
  // is visible before the scenario body. Blank/absent → omitted.
  if (typeof evalEntry.fixture === 'string' && evalEntry.fixture.trim()) {
    out.fixture = evalEntry.fixture.trim();
  }
  if (typeof evalEntry.target_base === 'string' && evalEntry.target_base.trim()) {
    out.target_base = evalEntry.target_base.trim();
  }
  // Per-run variables (gh #3007) — emit above input/expected so the reader sees
  // the fixture table before the `{{var()}}` references that consume it.
  if (evalEntry.variables && Object.keys(evalEntry.variables).length > 0) {
    out.variables = evalEntry.variables;
  }
  assertAttachmentsAreLocalPaths(evalEntry.input, `eval "${evalEntry.name}" input`);
  out.input = evalEntry.input;
  out.expected = evalEntry.expected;
  if (evalEntry.evaluator_instructions) {
    out.evaluator_instructions = evalEntry.evaluator_instructions;
  }
  return out;
}

export interface WriteHubFolderOptions {
  /**
   * Seed the per-hub `AGENTS.md` scaffold + `CLAUDE.md` shim (absence-guarded).
   * Default true. Pass false for the read-only production mirror — an "author
   * your hub memory here" scaffold has no place in a browse-only, never-synced
   * folder (and seeding only the shim would leave a dangling `@AGENTS.md` import).
   */
  seedAgentContext?: boolean;
}

/**
 * Write a HubAsCodePayload to disk as hub.yaml + agents/<slug>.yaml + agents/<slug>.md files.
 *
 * - Creates `hub.yaml` with hub config (no agents section)
 * - Creates `agents/<slug>.yaml` for each agent (all config except instructions)
 * - Creates `agents/<slug>.md` for each agent with instructions
 * - Seeds a per-hub `AGENTS.md` scaffold + `CLAUDE.md` shim unless `seedAgentContext` is false
 * - Removes orphan .yaml and .md files in agents/ that don't match any current agent
 * - Deletes old `wayai.yaml` if present (migration)
 *
 * Returns a FileChangeLog naming every working-tree path written (byte-identical writes are
 * skipped, so the write is idempotent) or removed, so callers can announce the delta (gh #3410).
 * The absence-guarded seeds (AGENTS.md/CLAUDE.md) are NOT recorded — they are one-time scaffolds,
 * not server-driven changes.
 */
export function writeHubFolder(
  hubFolder: string,
  payload: HubAsCodePayload,
  options: WriteHubFolderOptions = {},
): FileChangeLog {
  const log: FileChangeLog = { changed: [], removed: [] };

  // Ensure directories exist
  const agentsDir = path.join(hubFolder, 'agents');
  if (!fs.existsSync(hubFolder)) {
    fs.mkdirSync(hubFolder, { recursive: true });
  }
  if (!fs.existsSync(agentsDir)) {
    fs.mkdirSync(agentsDir, { recursive: true });
  }

  // Build YAML-safe payload (no agents section)
  const yamlPayload = buildYamlPayload(payload);
  const agentFiles = extractAgentFiles(payload);

  // Write hub.yaml
  const yamlContent = yaml.dump(yamlPayload, YAML_DUMP_OPTIONS);
  writeFileIfChanged(hubFolder, path.join(hubFolder, 'hub.yaml'), yamlContent, log);

  // Seed the hub's AGENTS.md (hub-specific memory the config files can't capture)
  // + a 1-line Claude Code shim that imports it. Absence-guarded — skipped once
  // the user/agent has authored them, so it's safe to run on every pull/push.
  // Other harnesses load AGENTS.md natively. Skipped for the read-only production
  // mirror (seedAgentContext=false): a browse-only folder is not an authoring surface.
  // Absence-guarded: written only when missing (never overwritten). A creation IS a working-tree
  // write, so record it — otherwise a metadata-sync that seeds these while every server file is
  // byte-identical would print "already in sync" after touching the tree. Fresh-materialize
  // callers already suppress the whole list, so recording here doesn't add noise there.
  if (options.seedAgentContext ?? true) {
    for (const seeded of [
      writeIfAbsent(path.join(hubFolder, 'AGENTS.md'), perHubAgentsMd(path.basename(hubFolder))),
      writeIfAbsent(path.join(hubFolder, 'CLAUDE.md'), CLAUDE_MD_SHIM),
    ]) {
      if (seeded) log.changed.push(seeded);
    }
  }

  // Delete old wayai.yaml if it exists (migration)
  const oldYamlPath = path.join(hubFolder, 'wayai.yaml');
  if (fs.existsSync(oldYamlPath)) {
    fs.unlinkSync(oldYamlPath);
    log.removed.push(path.relative(hubFolder, oldYamlPath));
  }

  // Write agent YAML config files + instruction files
  // Track .yaml and .md slugs separately so agents without instructions get their .md cleaned up
  const yamlSlugs = writeAgentYamlFiles(hubFolder, agentsDir, payload.agents || [], log);
  const mdSlugs = new Set<string>();
  for (const { slug, content } of agentFiles) {
    mdSlugs.add(slug);
    writeFileIfChanged(hubFolder, path.join(agentsDir, `${slug}.md`), content, log);
  }

  // Remove orphan .yaml and .md files
  const existingFiles = fs.readdirSync(agentsDir);
  for (const file of existingFiles) {
    if (file.endsWith('.yaml')) {
      const slug = file.slice(0, -5);
      if (!yamlSlugs.has(slug)) {
        const orphan = path.join(agentsDir, file);
        fs.unlinkSync(orphan);
        log.removed.push(path.relative(hubFolder, orphan));
      }
    } else if (file.endsWith('.md')) {
      const slug = file.slice(0, -3);
      if (!mdSlugs.has(slug)) {
        const orphan = path.join(agentsDir, file);
        fs.unlinkSync(orphan);
        log.removed.push(path.relative(hubFolder, orphan));
      }
    }
  }

  // Write resource files
  writeResourceFiles(hubFolder, payload.resources || [], log);

  // Write eval scenario files (always run — handles transition from "evals exist" to "no evals")
  writeEvalYamlFiles(hubFolder, payload.evals || [], log);

  // Write journey files (always run — handles transition from "journeys exist" to "no journeys")
  writeJourneyYamlFiles(hubFolder, payload.journeys || [], log);

  return log;
}

/**
 * Set or clear the top-level `preview_label` in a hub's `hub.yaml` in place,
 * preserving every other field. Used by `wayai relabel` so a label change touches
 * only the echoed metadata — never the agents/resources or any local edits a full
 * `writeHubFolder` would overwrite. Pass null to remove the key.
 *
 * Reads + re-dumps just `hub.yaml` with the same `YAML_DUMP_OPTIONS` the writer
 * uses (and `sortKeys: false`), so a synced file round-trips to itself; adding a
 * label that was previously absent appends the key (re-normalized on next pull).
 */
export function setPreviewLabelInHubYaml(hubFolder: string, label: string | null): void {
  const yamlPath = resolveHubYamlPath(hubFolder);
  if (!yamlPath) return;
  const obj = (yaml.load(fs.readFileSync(yamlPath, 'utf-8')) ?? {}) as Record<string, unknown>;
  if (label) obj.preview_label = label;
  else delete obj.preview_label;
  fs.writeFileSync(yamlPath, yaml.dump(obj, YAML_DUMP_OPTIONS), 'utf-8');
}

/**
 * Build the YAML-safe version of the payload (hub config only, no agents).
 * Agents are written to separate files by writeAgentYamlFiles().
 */
function buildYamlPayload(payload: HubAsCodePayload): Record<string, unknown> {
  const result: Record<string, unknown> = {
    version: payload.version,
    hub_id: payload.hub_id,
    hub_environment: payload.hub_environment,
  };

  // Echo back preview_label for preview hubs: read-only, server-owned metadata
  // (set via the backend replicate-preview / hub-update PATCH, not authored here).
  // Drives the `--<label>` hub-folder suffix (getHubFolderSlug in workspace.ts).
  if (payload.preview_label) {
    result.preview_label = payload.preview_label;
  }

  result.hub = payload.hub;

  // Agents are NOT included in hub.yaml — they live in agents/<slug>.yaml

  if (payload.resources && payload.resources.length > 0) {
    // Strip files from resource entries — filesystem is source of truth
    result.resources = payload.resources.map((r) => {
      const { files, ...rest } = r;
      return rest;
    });
  }

  if (payload.states && payload.states.length > 0) {
    result.states = payload.states;
  }

  if (payload.teams && payload.teams.length > 0) {
    result.teams = payload.teams;
  }

  if (payload.connections && payload.connections.length > 0) {
    result.connections = payload.connections;
  }

  if (payload.outbound_contacts && payload.outbound_contacts.length > 0) {
    result.outbound_contacts = payload.outbound_contacts;
  }

  if (payload.outbound_lists && payload.outbound_lists.length > 0) {
    result.outbound_lists = payload.outbound_lists;
  }

  if (payload.outbound_schedules && payload.outbound_schedules.length > 0) {
    result.outbound_schedules = payload.outbound_schedules;
  }

  return result;
}

/**
 * Write per-agent YAML config files to agents/ directory.
 * Each agent gets agents/<slug>.yaml with all config except instructions.
 * Returns the set of slugs written (for orphan cleanup).
 */
function writeAgentYamlFiles(hubFolder: string, agentsDir: string, agents: HubAsCodeAgent[], log: FileChangeLog): Set<string> {
  const slugs = new Set<string>();
  for (const agent of agents) {
    const slug = slugify(agent.name);
    slugs.add(slug);
    const entry: Record<string, unknown> = { ...agent };

    // Instructions go to .md file, not .yaml
    delete entry.instructions;

    const yamlContent = yaml.dump(entry, YAML_DUMP_OPTIONS);
    writeFileIfChanged(hubFolder, path.join(agentsDir, `${slug}.yaml`), yamlContent, log);
  }
  return slugs;
}

/**
 * Extract agent instruction files from the payload.
 */
function extractAgentFiles(
  payload: HubAsCodePayload
): Array<{ slug: string; content: string }> {
  const files: Array<{ slug: string; content: string }> = [];

  for (const agent of payload.agents || []) {
    if (agent.instructions) {
      files.push({
        slug: slugify(agent.name),
        content: agent.instructions,
      });
    }
  }

  return files;
}

// Writes evals/<slug>.yaml (root) or evals/<set>/<slug>.yaml; removes orphan .yaml files and empty set folders.
function writeEvalYamlFiles(hubFolder: string, evals: HubAsCodeEval[], log: FileChangeLog): void {
  const evalsDir = path.join(hubFolder, 'evals');

  // No evals: clean the directory if it exists, otherwise no-op.
  if (evals.length === 0) {
    if (fs.existsSync(evalsDir)) {
      cleanEvalOrphans(hubFolder, evalsDir, new Set<string>(), log);
      // Remove the now-empty top-level dir for tidiness.
      try {
        if (fs.readdirSync(evalsDir).length === 0) fs.rmdirSync(evalsDir);
      } catch { /* keep dir if non-empty (e.g. user-added files) */ }
    }
    return;
  }

  if (!fs.existsSync(evalsDir)) {
    fs.mkdirSync(evalsDir, { recursive: true });
  }

  // Track relative paths written (e.g. "greeting.yaml", "set-a/foo.yaml") for orphan cleanup.
  const writtenRelPaths = new Set<string>();

  for (const evalEntry of evals) {
    const slug = slugify(evalEntry.name);
    const setName = evalEntry.path && evalEntry.path.trim().length > 0 ? evalEntry.path : null;
    const relPath = setName ? `${setName}/${slug}.yaml` : `${slug}.yaml`;
    const targetPath = path.join(evalsDir, relPath);

    // Defense-in-depth: the backend's `validateScenarioSetName` doesn't
    // restrict path separators, so refuse to write any path that escapes
    // evals/. Same pattern as `cleanOrphanFiles` for resource files.
    if (!targetPath.startsWith(evalsDir + path.sep)) {
      console.warn(`  Warning: skipping eval "${evalEntry.name}" (scenario set "${setName}" escapes evals/ — possible bad backend data)`);
      continue;
    }

    if (setName) {
      const setDir = path.join(evalsDir, setName);
      if (!fs.existsSync(setDir)) {
        fs.mkdirSync(setDir, { recursive: true });
      }
    }

    const yamlContent = yaml.dump(buildEvalYamlObject(evalEntry, slug), YAML_DUMP_OPTIONS);
    writeFileIfChanged(hubFolder, targetPath, yamlContent, log);
    writtenRelPaths.add(relPath);
  }

  cleanEvalOrphans(hubFolder, evalsDir, writtenRelPaths, log);
}

// Only touches .yaml files (user-added READMEs/notes are preserved); empty set subfolders are pruned.
function cleanEvalOrphans(hubFolder: string, evalsDir: string, writtenRelPaths: Set<string>, log: FileChangeLog): void {
  const entries = fs.readdirSync(evalsDir, { withFileTypes: true });

  for (const entry of entries) {
    const fullPath = path.join(evalsDir, entry.name);

    if (entry.isFile()) {
      if (entry.name.endsWith('.yaml') && !writtenRelPaths.has(entry.name)) {
        fs.unlinkSync(fullPath);
        log.removed.push(path.relative(hubFolder, fullPath));
      }
      continue;
    }

    if (entry.isDirectory()) {
      const setName = entry.name;
      const subEntries = fs.readdirSync(fullPath, { withFileTypes: true });
      for (const sub of subEntries) {
        if (sub.isFile() && sub.name.endsWith('.yaml')) {
          const relPath = `${setName}/${sub.name}`;
          if (!writtenRelPaths.has(relPath)) {
            const orphan = path.join(fullPath, sub.name);
            fs.unlinkSync(orphan);
            log.removed.push(path.relative(hubFolder, orphan));
          }
        }
        // Nested directories aren't valid eval layout — leave them alone.
      }
      // Remove now-empty scenario-set folder.
      try {
        if (fs.readdirSync(fullPath).length === 0) fs.rmdirSync(fullPath);
      } catch { /* ignore — non-empty (e.g. user notes) */ }
    }
  }
}

// Shared between writeJourneyYamlFiles (pull) and `wayai journey capture` so the
// on-disk shape stays identical. Field order mirrors the journey YAML design:
// id, name (omitted when == slug), agent, agent_id, enabled, evaluator_instructions,
// variables, transcript, step_config.
export function buildJourneyYamlObject(journeyEntry: HubAsCodeJourney, slug: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (journeyEntry.id) out.id = journeyEntry.id;
  if (journeyEntry.name !== slug) out.name = journeyEntry.name;
  out.agent = journeyEntry.agent;
  if (journeyEntry.agent_id) out.agent_id = journeyEntry.agent_id;
  if (journeyEntry.enabled === false) {
    out.enabled = false;
  }
  if (journeyEntry.evaluator_instructions) {
    out.evaluator_instructions = journeyEntry.evaluator_instructions;
  }
  // Eval-session seed fixture (gh #3090) — the journey owns its set, so this is the
  // set-level seed dependency. Blank/absent → omitted.
  if (typeof journeyEntry.fixture === 'string' && journeyEntry.fixture.trim()) {
    out.fixture = journeyEntry.fixture.trim();
  }
  if (typeof journeyEntry.target_base === 'string' && journeyEntry.target_base.trim()) {
    out.target_base = journeyEntry.target_base.trim();
  }
  // Per-run variables (gh #3007) — emit above the transcript so the fixture table
  // reads before the `{{var()}}` references that consume it.
  if (journeyEntry.variables && Object.keys(journeyEntry.variables).length > 0) {
    out.variables = journeyEntry.variables;
  }
  if (journeyEntry.transcript && journeyEntry.transcript.length > 0) {
    for (const turn of journeyEntry.transcript) assertAttachmentsAreLocalPaths(turn, `journey "${journeyEntry.name}" transcript`);
    out.transcript = journeyEntry.transcript;
  }
  if (journeyEntry.step_config && Object.keys(journeyEntry.step_config).length > 0) {
    out.step_config = journeyEntry.step_config;
  }
  return out;
}

// Writes journeys/<slug>.yaml (flat folder — no scenario-set subfolders);
// removes orphan .yaml files. Same orphan-cleanup discipline as evals.
function writeJourneyYamlFiles(hubFolder: string, journeys: HubAsCodeJourney[], log: FileChangeLog): void {
  const journeysDir = path.join(hubFolder, 'journeys');

  // No journeys: clean the directory if it exists, otherwise no-op.
  if (journeys.length === 0) {
    if (fs.existsSync(journeysDir)) {
      cleanJourneyOrphans(hubFolder, journeysDir, new Set<string>(), log);
      try {
        if (fs.readdirSync(journeysDir).length === 0) fs.rmdirSync(journeysDir);
      } catch { /* keep dir if non-empty (e.g. user-added files) */ }
    }
    return;
  }

  if (!fs.existsSync(journeysDir)) {
    fs.mkdirSync(journeysDir, { recursive: true });
  }

  const writtenFiles = new Set<string>();
  // Disambiguate distinct journey names that slugify to the same value (e.g.
  // "Cancel Order" and "cancel order!") — without this they'd overwrite the same
  // file on pull and silently lose one journey. ensureUniqueSlug appends _2/_3…;
  // when the slug is disambiguated it diverges from name, so buildJourneyYamlObject
  // emits the `name` field, keeping the round-trip lossless.
  const usedSlugs = new Set<string>();
  for (const journeyEntry of journeys) {
    const slug = ensureUniqueSlug(slugify(journeyEntry.name), usedSlugs);
    const fileName = `${slug}.yaml`;
    const yamlContent = yaml.dump(buildJourneyYamlObject(journeyEntry, slug), YAML_DUMP_OPTIONS);
    writeFileIfChanged(hubFolder, path.join(journeysDir, fileName), yamlContent, log);
    writtenFiles.add(fileName);
  }

  cleanJourneyOrphans(hubFolder, journeysDir, writtenFiles, log);
}

// Only touches .yaml files (user-added READMEs/notes are preserved). Journeys are
// a flat folder, so there are no set subfolders to prune.
function cleanJourneyOrphans(hubFolder: string, journeysDir: string, writtenFiles: Set<string>, log: FileChangeLog): void {
  const entries = fs.readdirSync(journeysDir, { withFileTypes: true });
  for (const entry of entries) {
    if (entry.isFile() && entry.name.endsWith('.yaml') && !writtenFiles.has(entry.name)) {
      const orphan = path.join(journeysDir, entry.name);
      fs.unlinkSync(orphan);
      log.removed.push(path.relative(hubFolder, orphan));
    }
  }
}

/**
 * Write resource files to disk and clean up orphan resource directories.
 */
function writeResourceFiles(hubFolder: string, resources: HubAsCodeResource[], log: FileChangeLog): void {
  const resourcesDir = path.join(hubFolder, 'resources');

  // Track current resource slugs for orphan cleanup
  const currentSlugs = new Set<string>();

  for (const resource of resources) {
    const resSlug = slugify(resource.name);
    currentSlugs.add(resSlug);
    const resDir = path.join(resourcesDir, resSlug);
    writeResourceFileTree(resDir, resource.files || [], hubFolder, log);
  }

  // Remove orphan resource directories. A recursively-removed dir is reported as
  // "<dir>/" — we do NOT walk it to enumerate the children being deleted.
  if (fs.existsSync(resourcesDir)) {
    const existingDirs = fs.readdirSync(resourcesDir, { withFileTypes: true });
    for (const entry of existingDirs) {
      if (entry.isDirectory() && !currentSlugs.has(entry.name)) {
        const orphanDir = path.join(resourcesDir, entry.name);
        fs.rmSync(orphanDir, { recursive: true, force: true });
        log.removed.push(`${path.relative(hubFolder, orphanDir)}/`);
      }
    }
  }
}
